import { InputNumber } from '@teo/components';
import { useState, useEffect, Fragment } from 'react';
import PaginationNew from './PaginationNew';
import { parseFilter } from '@/util';
import EmptyTable from './EmptyTable';
import { Listbox, Transition } from '@headlessui/react';
import { clsx } from 'clsx';
import { ChevronDownFilled } from '@teo/components/icons';
import { useTranslation } from 'react-i18next';

export const PaginationAnythingNew = ({
  fetch,
  filter,
  defaultSort = [],
  page = 0,
  pageSize = 10,
  setContentRange,
  render,
  empty,
  ...other
}: any) => {
  const { t } = useTranslation();
  const [selectMode, setSelectMode] = useState(true);

  const [offset, setOffset] = useState(page * pageSize);
  const [count, setCount] = useState(pageSize);

  const [data, setData] = useState([]);
  const [maxPage, setMaxPage] = useState(1);

  const [sorted, setSorted] = useState(defaultSort || []);
  const [sortQuery, setSortQuery] = useState(
    sorted.map((sort: any) => (sort.desc ? '-' : '') + sort.id).join(',')
  );

  const showOptions = [
    {
      value: 10,
      label: '10',
    },
    {
      value: 20,
      label: '20',
    },
    {
      value: 40,
      label: '40',
    },
    {
      value: 80,
      label: '80',
    },
  ];

  const [showType, setShowType] = useState(showOptions[0]);

  useEffect(() => {
    setSorted(defaultSort);
    setSortQuery(
      defaultSort.map((sort: any) => (sort.desc ? '-' : '') + sort.id).join(',')
    );
  }, [defaultSort]);

  page = Math.floor(offset / count) + 1;

  const addSort = (colId: number | string) => {
    const index = sorted.findIndex((x: any) => x.id === colId);
    if (index > -1) {
      const sort = sorted[index];
      sorted.splice(index, 1);
      sorted.unshift({ id: colId, desc: !sort.desc });
    } else {
      sorted.unshift({ id: colId, desc: true });
    }
    setSorted(sorted);
    setSortQuery(
      sorted.map((sort: any) => (sort.desc ? '-' : '') + sort.id).join(',')
    );
  };

  const result: any = fetch({
    offset,
    count,
    sort: sortQuery ? sortQuery : undefined,
    q: parseFilter(filter),
  });

  useEffect(() => {
    if (result?.data) {
      setData(result?.data?.data);
      const contentRange = result?.data?.headers?.['content-range']
        ?.split(' ')?.[1]
        ?.split('/');
      const maxCount = contentRange ? Math.ceil(contentRange?.[1]) : 1;
      setMaxPage(Math.ceil(maxCount / count));
      setContentRange && setContentRange(contentRange?.[1]);
    }
  }, [result?.data]);

  useEffect(() => {
    if (other?.tab) {
      setOffset(0);
    }
  }, [other?.tab]);

  return (
    <>
      {data?.length ? (
        <>
          <div {...other}>
            {data?.map((el: any, index: any) => (
              <Fragment key={index}>{render(el)}</Fragment>
            ))}
          </div>
          {maxPage > 1 && (
            <div className="flex w-full items-center justify-between">
              <div>
                <PaginationNew
                  setOffset={setOffset}
                  offset={offset}
                  count={count}
                  page={page}
                  maxPage={maxPage}
                />
              </div>
              <div className="flex items-center">
                <div className="ml-2 hidden items-center gap-1 lg:flex">
                  <p className="mr-1 text-sm text-grey-07">
                    {t('pagination.go_to_page')}
                  </p>
                  <InputNumber
                    mode="integer"
                    value={page}
                    onChange={(value: any) => {
                      if (value > 0) {
                        setOffset((value - 1) * count);
                      }
                    }}
                    min={1}
                    max={maxPage}
                    className="max-w-[50px]"
                  />
                </div>
                <div className="ml-2 flex items-center gap-1">
                  <p className="mr-1 text-sm text-grey-07">
                    {t('pagination.show')}
                  </p>
                  <div>
                    <Listbox
                      value={showType}
                      onChange={(newShowType) => {
                        setShowType(newShowType);
                        setCount(newShowType?.value);
                        setOffset(
                          Math.floor(offset / newShowType?.value) *
                            newShowType?.value
                        );
                      }}
                    >
                      {({ open }) => (
                        <div>
                          <Listbox.Button
                            className={clsx(
                              'flex items-center justify-between',
                              'rounded-md border border-solid outline outline-2',
                              'min-h-10 w-full min-w-[70px] px-2 text-base',
                              {
                                'border-secondary-04 bg-white outline-secondary-01':
                                  open,
                                'border-transparent bg-grey-transparent-01 text-grey-08 outline-transparent hover:border-grey-transparent-03 hover:bg-white hover:outline-grey-transparent-01 focus:border-secondary-04 focus:bg-white focus:outline-secondary-01':
                                  !open,
                              }
                            )}
                          >
                            <span className="mr-2 block truncate">
                              {!showType?.label ? '' : showType?.label}
                            </span>
                            <ChevronDownFilled
                              className={clsx(
                                'h-5 w-5',
                                'transition-transform',
                                open && 'rotate-180'
                              )}
                            />
                          </Listbox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition-opacity"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <div className="listbox relative z-10">
                              <Listbox.Options className="absolute bottom-0 mt-1  flex max-h-40 w-full -translate-y-[42px] cursor-pointer flex-col gap-1 overflow-auto rounded-lg bg-white p-1 drop-shadow-s focus:outline-none">
                                {showOptions.map((option, optionIndex) => (
                                  <Listbox.Option
                                    key={optionIndex}
                                    className={({ active }) =>
                                      clsx(
                                        'rounded-md px-3 py-2 text-base text-black',
                                        active && 'bg-grey-transparent-02'
                                      )
                                    }
                                    value={option}
                                  >
                                    {({ selected }) => (
                                      <span
                                        className={clsx(
                                          'block',
                                          selected && 'font-medium'
                                        )}
                                      >
                                        <span>{option?.label}</span>
                                      </span>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </div>
                          </Transition>
                        </div>
                      )}
                    </Listbox>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className={`${empty?.loadContent === true && ' hidden '}`}>
          <EmptyTable
            template={empty?.template}
            text={empty?.text}
            btn_text={empty?.btn_text}
            btnClick={() => empty?.btnClick && empty?.btnClick()}
            filter={filter}
          />
        </div>
      )}
    </>
  );
};
